import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import { organizationService } from '../../services/organizationService';

const CompanyRegistrationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Information
    organizationType: '', // 'corporation', 'nonprofit', 'social_enterprise\'organizationName: '',industry: '',organizationSize: '',website: '',description: '',
    
    // Contact Information
    contactName: '',contactTitle: '',contactEmail: user?.email || '',contactPhone: '',
    
    // Address Information
    streetAddress: '',city: '',state: '',zipCode: '',country: 'United States',
    
    // Legal Information
    taxId: '',is501c3: false,ein: '',
    
    // ESG Focus Areas
    esgFocusAreas: [],
    sustainabilityGoals: '',
    
    // Terms and Agreements
    agreeToTerms: false,
    agreeToDataSharing: false
  });

  const [errors, setErrors] = useState({});

  const organizationTypes = [
    { value: 'corporation', label: 'Corporation' },
    { value: 'nonprofit', label: 'Nonprofit Organization' },
    { value: 'social_enterprise', label: 'Social Enterprise' },
    { value: 'foundation', label: 'Foundation' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance & Banking' },
    { value: 'education', label: 'Education' },
    { value: 'environment', label: 'Environment & Sustainability' },
    { value: 'social_services', label: 'Social Services' },
    { value: 'arts_culture', label: 'Arts & Culture' },
    { value: 'advocacy', label: 'Advocacy & Human Rights' },
    { value: 'international', label: 'International Development' },
    { value: 'disaster_relief', label: 'Disaster Relief' },
    { value: 'other', label: 'Other' }
  ];

  const organizationSizes = [
    { value: 'startup', label: 'Startup (1-10 employees)' },
    { value: 'small', label: 'Small (11-50 employees)' },
    { value: 'medium', label: 'Medium (51-200 employees)' },
    { value: 'large', label: 'Large (201-1000 employees)' },
    { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
  ];

  const esgFocusOptions = [
    { value: 'environmental', label: 'Environmental Sustainability' },
    { value: 'social', label: 'Social Impact' },
    { value: 'governance', label: 'Corporate Governance' },
    { value: 'diversity', label: 'Diversity & Inclusion' },
    { value: 'climate', label: 'Climate Action' },
    { value: 'community', label: 'Community Development' },
    { value: 'education', label: 'Education & Training' },
    { value: 'health', label: 'Health & Wellbeing' },
    { value: 'poverty', label: 'Poverty Alleviation' },
    { value: 'human_rights', label: 'Human Rights' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEsgFocusChange = (value) => {
    const updatedFocus = formData?.esgFocusAreas?.includes(value)
      ? formData?.esgFocusAreas?.filter(item => item !== value)
      : [...(formData?.esgFocusAreas || []), value];
    
    setFormData(prev => ({
      ...prev,
      esgFocusAreas: updatedFocus
    }));
  };

  const validateStep = (currentStep) => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData?.organizationType) newErrors.organizationType = 'Organization type is required';
      if (!formData?.organizationName) newErrors.organizationName = 'Organization name is required';
      if (!formData?.industry) newErrors.industry = 'Industry is required';
      if (!formData?.organizationSize) newErrors.organizationSize = 'Organization size is required';
      if (!formData?.description) newErrors.description = 'Description is required';
    } else if (currentStep === 2) {
      if (!formData?.contactName) newErrors.contactName = 'Contact name is required';
      if (!formData?.contactTitle) newErrors.contactTitle = 'Contact title is required';
      if (!formData?.contactEmail) newErrors.contactEmail = 'Contact email is required';
      if (!formData?.streetAddress) newErrors.streetAddress = 'Street address is required';
      if (!formData?.city) newErrors.city = 'City is required';
      if (!formData?.state) newErrors.state = 'State is required';
      if (!formData?.zipCode) newErrors.zipCode = 'ZIP code is required';
    } else if (currentStep === 3) {
      if (formData?.is501c3 && !formData?.ein) {
        newErrors.ein = 'EIN is required for 501(c)(3) organizations';
      }
    } else if (currentStep === 4) {
      if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    setLoading(true);
    
    try {
      const organizationData = {
        name: formData?.organizationName,
        type: formData?.organizationType,
        industry: formData?.industry,
        organization_size: formData?.organizationSize,
        website: formData?.website,
        description: formData?.description,
        contact_name: formData?.contactName,
        contact_title: formData?.contactTitle,
        contact_email: formData?.contactEmail,
        contact_phone: formData?.contactPhone,
        address: {
          street: formData?.streetAddress,
          city: formData?.city,
          state: formData?.state,
          zip_code: formData?.zipCode,
          country: formData?.country
        },
        tax_id: formData?.taxId,
        is_501c3: formData?.is501c3,
        ein: formData?.ein,
        esg_focus_areas: formData?.esgFocusAreas,
        sustainability_goals: formData?.sustainabilityGoals,
        status: 'pending_verification'
      };

      const { data, error } = await organizationService?.createOrganization(organizationData);

      if (error) {
        setErrors({ general: error?.message || 'Failed to create organization' });
        return;
      }

      // Navigate to organization dashboard or confirmation page
      navigate('/organization-dashboard', {
        state: { message: 'Organization profile created successfully! Your account is pending verification.' }
      });

    } catch (error) {
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Icon name="Building2" size={36} color="white" />
        </div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent mb-2">Organization Information</h2>
        <p className="text-gray-600">Tell us about your organization</p>
      </div>

      <Select
        label="Organization Type"
        name="organizationType"
        value={formData?.organizationType}
        onChange={handleInputChange}
        options={organizationTypes}
        error={errors?.organizationType}
        required
      />

      <Input
        label="Organization Name"
        name="organizationName"
        value={formData?.organizationName}
        onChange={handleInputChange}
        placeholder="Enter your organization name"
        error={errors?.organizationName}
        required
      />

      <Select
        label="Industry"
        name="industry"
        value={formData?.industry}
        onChange={handleInputChange}
        options={industries}
        error={errors?.industry}
        required
      />

      <Select
        label="Organization Size"
        name="organizationSize"
        value={formData?.organizationSize}
        onChange={handleInputChange}
        options={organizationSizes}
        error={errors?.organizationSize}
        required
      />

      <Input
        label="Website (Optional)"
        name="website"
        type="url"
        value={formData?.website}
        onChange={handleInputChange}
        placeholder="https://www.example.com"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Organization Description *
        </label>
        <textarea
          name="description"
          rows={4}
          value={formData?.description}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none transition-all duration-200 shadow-sm"
          placeholder="Describe your organization's mission and activities"
        />
        {errors?.description && (
          <p className="mt-1 text-sm text-red-600">{errors?.description}</p>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Icon name="User" size={36} color="white" />
        </div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent mb-2">Contact Information</h2>
        <p className="text-gray-600">Primary contact details</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Contact Name"
          name="contactName"
          value={formData?.contactName}
          onChange={handleInputChange}
          placeholder="Full name"
          error={errors?.contactName}
          required
        />

        <Input
          label="Job Title"
          name="contactTitle"
          value={formData?.contactTitle}
          onChange={handleInputChange}
          placeholder="e.g., CEO, Director"
          error={errors?.contactTitle}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Email Address"
          name="contactEmail"
          type="email"
          value={formData?.contactEmail}
          onChange={handleInputChange}
          placeholder="contact@organization.com"
          error={errors?.contactEmail}
          required
        />

        <Input
          label="Phone Number"
          name="contactPhone"
          type="tel"
          value={formData?.contactPhone}
          onChange={handleInputChange}
          placeholder="(555) 123-4567"
        />
      </div>

      <div className="border-t pt-6 bg-gradient-to-r from-orange-50 to-pink-50 -mx-8 px-8 py-6 rounded-xl">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Icon name="MapPin" size={20} className="text-orange-500 mr-2" />
          Organization Address
        </h3>
        
        <Input
          label="Street Address"
          name="streetAddress"
          value={formData?.streetAddress}
          onChange={handleInputChange}
          placeholder="123 Main Street"
          error={errors?.streetAddress}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Input
            label="City"
            name="city"
            value={formData?.city}
            onChange={handleInputChange}
            placeholder="City"
            error={errors?.city}
            required
          />

          <Input
            label="State/Province"
            name="state"
            value={formData?.state}
            onChange={handleInputChange}
            placeholder="State"
            error={errors?.state}
            required
          />

          <Input
            label="ZIP/Postal Code"
            name="zipCode"
            value={formData?.zipCode}
            onChange={handleInputChange}
            placeholder="12345"
            error={errors?.zipCode}
            required
          />
        </div>

        <Input
          label="Country"
          name="country"
          value={formData?.country}
          onChange={handleInputChange}
          className="mt-4"
          readOnly
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Icon name="Shield" size={36} color="white" />
        </div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">Legal & ESG Information</h2>
        <p className="text-gray-600">Verification and focus areas</p>
      </div>

      <Input
        label="Tax ID (Optional)"
        name="taxId"
        value={formData?.taxId}
        onChange={handleInputChange}
        placeholder="Tax identification number"
      />

      <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-xl border border-green-200 shadow-sm">
        <Checkbox
          label="This is a 501(c)(3) tax-exempt organization"
          name="is501c3"
          checked={formData?.is501c3}
          onChange={handleInputChange}
        />
        
        {formData?.is501c3 && (
          <div className="mt-4">
            <Input
              label="EIN (Employer Identification Number)"
              name="ein"
              value={formData?.ein}
              onChange={handleInputChange}
              placeholder="12-3456789"
              error={errors?.ein}
              required
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4 flex items-center">
          <Icon name="Target" size={18} className="text-orange-500 mr-2" />
          ESG Focus Areas (Select all that apply)
        </label>
        <div className="grid grid-cols-2 gap-3">
          {esgFocusOptions?.map((option) => (
            <div 
              key={option?.value}
              className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                formData?.esgFocusAreas?.includes(option?.value)
                  ? 'border-orange-400 bg-orange-50' :'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleEsgFocusChange(option?.value)}
            >
              <Checkbox
                label={option?.label}
                checked={formData?.esgFocusAreas?.includes(option?.value)}
                onChange={() => handleEsgFocusChange(option?.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          <Icon name="Lightbulb" size={18} className="text-teal-500 mr-2" />
          Sustainability Goals (Optional)
        </label>
        <textarea
          name="sustainabilityGoals"
          rows={3}
          value={formData?.sustainabilityGoals}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none transition-all duration-200 shadow-sm"
          placeholder="Describe your organization's sustainability goals and commitments"
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Icon name="CheckCircle" size={36} color="white" />
        </div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent mb-2">Review & Submit</h2>
        <p className="text-gray-600">Confirm your information and agree to terms</p>
      </div>

      {/* Organization Summary */}
      <div className="bg-gradient-to-r from-orange-50 to-pink-50 p-6 rounded-xl border border-orange-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Icon name="FileText" size={20} className="text-orange-500 mr-2" />
          Organization Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Name:</span> 
            <span className="text-gray-900 ml-2">{formData?.organizationName}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Type:</span> 
            <span className="text-gray-900 ml-2">{organizationTypes?.find(t => t?.value === formData?.organizationType)?.label}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Industry:</span> 
            <span className="text-gray-900 ml-2">{industries?.find(i => i?.value === formData?.industry)?.label}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Size:</span> 
            <span className="text-gray-900 ml-2">{organizationSizes?.find(s => s?.value === formData?.organizationSize)?.label}</span>
          </div>
          <div className="md:col-span-2">
            <span className="font-medium text-gray-700">Contact:</span> 
            <span className="text-gray-900 ml-2">{formData?.contactName} ({formData?.contactEmail})</span>
          </div>
          {formData?.is501c3 && (
            <div className="md:col-span-2 bg-green-100 p-3 rounded-lg">
              <span className="font-medium text-green-800 flex items-center">
                <Icon name="Shield" size={16} className="mr-1" />
                501(c)(3) Status: Verified
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="space-y-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="font-medium text-gray-800 mb-3">Terms & Agreements</h4>
        
        <div className={`p-4 rounded-lg border-2 transition-all ${
          formData?.agreeToTerms ? 'border-green-300 bg-green-50' : 'border-gray-200'
        }`}>
          <Checkbox
            label="I agree to the Terms of Service and Privacy Policy"
            name="agreeToTerms"
            checked={formData?.agreeToTerms}
            onChange={handleInputChange}
            error={errors?.agreeToTerms}
            required
          />
        </div>

        <div className={`p-4 rounded-lg border-2 transition-all ${
          formData?.agreeToDataSharing ? 'border-teal-300 bg-teal-50' : 'border-gray-200'
        }`}>
          <Checkbox
            label="I consent to data sharing for verification and impact reporting"
            name="agreeToDataSharing"
            checked={formData?.agreeToDataSharing}
            onChange={handleInputChange}
          />
        </div>
      </div>

      {errors?.general && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-2" />
            <p className="text-sm text-red-600">{errors?.general}</p>
          </div>
        </div>
      )}
    </div>
  );

  const steps = [
    { number: 1, title: 'Organization', component: renderStep1 },
    { number: 2, title: 'Contact Info', component: renderStep2 },
    { number: 3, title: 'Legal & ESG', component: renderStep3 },
    { number: 4, title: 'Review', component: renderStep4 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-teal-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                <Icon name="Heart" size={20} color="white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                  Share Good
                </h1>
                <p className="text-sm text-gray-600">ESG Impact Platform</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center">
            {steps?.map((stepItem, index) => (
              <div key={stepItem?.number} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-3 shadow-lg transition-all duration-300 ${
                  step >= stepItem?.number
                    ? 'bg-gradient-to-br from-orange-500 to-pink-500 border-orange-400 text-white transform scale-110'
                    : 'bg-white border-gray-300 text-gray-500'
                }`}>
                  {step > stepItem?.number ? (
                    <Icon name="Check" size={20} />
                  ) : (
                    <span className="font-bold">{stepItem?.number}</span>
                  )}
                </div>
                {index < steps?.length - 1 && (
                  <div className={`w-20 h-2 mx-4 rounded-full transition-all duration-300 ${
                    step > stepItem?.number ? 'bg-gradient-to-r from-orange-400 to-pink-400' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-6">
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-800">
                {steps?.find(s => s?.number === step)?.title}
              </p>
              <p className="text-sm text-gray-600">
                Step {step} of {steps?.length}
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {steps?.find(s => s?.number === step)?.component()}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-10 pt-8 border-t border-gray-100">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  iconName="ChevronLeft"
                  iconPosition="left"
                  className="bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border-gray-300"
                >
                  Previous
                </Button>
              ) : (
                <div />
              )}

              {step < steps?.length ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  iconName="ChevronRight"
                  iconPosition="right"
                  className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  loading={loading}
                  iconName="Send"
                  iconPosition="left"
                  className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  {loading ? 'Creating Organization...' : 'Create Organization'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegistrationForm;